import { Maalepunkt } from "codegen";
import { useState } from "react";
import styled from "styled-components";

import { colors } from "@ntemarked/mittne-components";
import Button from "@ntemarked/mittne-components/dist/Button/Button";

import { useStrombestillingApiQueries, useStrombestillingApiQuery } from "hooks/useStrombestillingApi";
import { WarningBox } from "pages/Sikring/components/InfoBox";
import { Hint, Label } from "pages/Sikring/opprett/CreateSikringPage";
import {
	CustomerMeteringPointRow,
	MeteringPointCheck,
	ParsedMaalepunktIds,
	buildCustomerMeteringPointList,
	isSelectable,
	parseMaalepunktIds,
} from "pages/Sikring/opprett/customerMeteringPointList";
import { formatNumber, isDefined } from "pages/Sikring/sikringUtils";
import { SikringMaalepunkt } from "pages/Sikring/types";
import { formatAddress } from "utils/Address";
import { isDevEnv } from "utils/Utils";

/**
 * Målepunktene på kundens aktive kontrakter i prisområdet - samme utvalg backend bruker når ordren opprettes.
 * Brukes både av listen i steg 1 og av veiviseren (sperre steget og antall i oppsummeringen); React Query
 * deler resultatet mellom dem, så det blir bare ett kall.
 */
export function useCustomerMeteringPoints(orgnr: string, prisomraade: string, enabled: boolean = true) {
	const params = new URLSearchParams({ organisasjonsnummer: orgnr, prisomraade });
	return useStrombestillingApiQuery<SikringMaalepunkt[]>(
		`/sikringsordre/kundenivaa/maalepunkter?${params}`,
		["sikringMaalepunkter", orgnr, prisomraade],
		enabled && !!orgnr && !!prisomraade
	);
}

/**
 * Slår opp målepunktene brukeren har lagt til for hånd, ett kall per målepunkt, for å sjekke at de finnes og ligger i
 * prisområdet. Brukes både av listen og av veiviseren; React Query deler resultatene mellom dem.
 */
export function useAddedMeteringPointLookups(ids: string[]) {
	const results = useStrombestillingApiQueries<Maalepunkt[]>(
		ids.map((id) => ({ endpoint: `/maalepunkter?${new URLSearchParams({ maalepunktId: id })}`, key: ["maalepunkter", "maalepunktId", id] }))
	);
	return new Map(ids.map((id, i) => [id, results[i]]));
}

/**
 * Uten kontrakt må alle målepunktene ha sikringsproduktet. I produksjon stopper et målepunkt uten produktet bestillingen,
 * i dev og lokalt kan brukeren likevel gå videre.
 */
export function canProceedWithoutSikringsprodukt(): boolean {
	return isDevEnv();
}

interface CustomerMeteringPointsProps {
	orgnr: string;
	prisomraade: string;
	/** Målepunkt-ID-er brukeren har lagt til for hånd, i tillegg til kundens eksisterende målepunkt. */
	addedMeteringPointIds: string[];
	/** Uten kontrakt gjelder sikringen alle kundens eksisterende målepunkt, så da kan ingen legges til eller ekskluderes. */
	editable: boolean;
	/** Målepunkt-ID-er brukeren har ekskludert fra sikringen. */
	excludedMeteringPointIds: string[];
	setAddedMeteringPointIds: (ids: string[]) => void;
	setExcludedMeteringPointIds: (ids: string[]) => void;
}

/**
 * Målepunktene en sikring på kundenivå skal omfatte. Listen forhåndsutfylles med målepunktene på kundens aktive
 * kontrakter i prisområdet. Brukeren kan velge hvilke som skal inkluderes, og legge til flere ved å lime inn
 * målepunkt-ID-er, f.eks. en kolonne fra Excel. Uten valgte målepunkt sperrer veiviseren steget.
 */
export function CustomerMeteringPoints({
	orgnr,
	prisomraade,
	addedMeteringPointIds,
	editable,
	excludedMeteringPointIds,
	setAddedMeteringPointIds,
	setExcludedMeteringPointIds,
}: Readonly<CustomerMeteringPointsProps>) {
	const { data: existing, isPending, isError, refetch } = useCustomerMeteringPoints(orgnr, prisomraade);
	const lookups = useAddedMeteringPointLookups(addedMeteringPointIds);
	const [adding, setAdding] = useState(false);
	const [pasted, setPasted] = useState("");
	const [lastAdded, setLastAdded] = useState<ParsedMaalepunktIds | null>(null);

	if (isPending) return <Hint style={{ margin: 0 }}>Henter kundens målepunkt i {prisomraade} …</Hint>;

	if (isError) {
		return (
			<ErrorText>
				Kunne ikke hente kundens målepunkt i {prisomraade}.{" "}
				<a
					href="#"
					onClick={(e) => {
						e.preventDefault();
						refetch();
					}}
				>
					Prøv igjen
				</a>
			</ErrorText>
		);
	}

	// Listen kan bare endres når ordren oppretter kontrakt, og bare da avvises målepunkt som er blokkert for bytte.
	const rows = buildCustomerMeteringPointList(existing, addedMeteringPointIds, excludedMeteringPointIds, lookups, prisomraade, editable);
	const selectedCount = rows.filter((mp) => mp.selected).length;
	// Uten kontrakt (listen kan ikke endres) endres ikke produktet på målepunktene, så det vises om de allerede har sikringsproduktet.
	const missingProductCount = editable ? 0 : rows.filter((mp) => !mp.harSikringsprodukt).length;
	// "Velg alle" gjelder bare målepunkt som kan velges, så de som aldri kan være med ikke holder den halvt avkrysset.
	const selectableRows = rows.filter(isSelectable);
	const checkedCount = selectableRows.filter((mp) => mp.checked).length;

	function setChecked(ids: string[], checked: boolean) {
		const changed = new Set(ids);
		const rest = excludedMeteringPointIds.filter((id) => !changed.has(id));
		setExcludedMeteringPointIds(checked ? rest : [...rest, ...ids]);
	}

	function addPasted() {
		const parsed = parseMaalepunktIds(
			pasted,
			rows.map((mp) => mp.maalepunktId)
		);
		setAddedMeteringPointIds([...addedMeteringPointIds, ...parsed.newIds]);
		setLastAdded(parsed);
		setPasted("");
		setAdding(false);
	}

	return (
		<div>
			{existing.length === 0 &&
				(editable ? (
					<InfoText>Kunden har ingen aktive kontrakter i {prisomraade}. Legg til målepunkt under.</InfoText>
				) : (
					<ErrorText>
						Kunden har ingen aktive kontrakter i {prisomraade}. Uten kontrakt kan sikringen bare gjelde målepunkt NTE allerede leverer til.
					</ErrorText>
				))}
			{missingProductCount > 0 && (
				<MissingProductWarning>
					<WarningBox
						heading={
							missingProductCount === rows.length
								? "Ingen av målepunktene har sikringsproduktet."
								: `${missingProductCount} av ${rows.length} målepunkt har ikke sikringsproduktet.`
						}
						body={
							canProceedWithoutSikringsprodukt()
								? "Målerne må ha sikringsproduktet for at de skal være en del av sikringen. Sjekk at ingen målere mangler."
								: "Målerne må ha sikringsproduktet for at de skal være en del av sikringen. Uten kontrakt kan du ikke gå videre før alle målerne har produktet."
						}
					/>
				</MissingProductWarning>
			)}
			{rows.length > 0 && (
				<>
					<Hint style={{ margin: "0 0 8px" }}>
						{editable
							? `Sikringen omfatter ${selectedCount} av ${rows.length} målepunkt i ${prisomraade}:`
							: // Uten kontrakt omfatter sikringen bare målepunktene som allerede har sikringsproduktet.
								`Sikringen omfatter ${rows.length - missingProductCount} av ${rows.length} målepunkt i ${prisomraade}:`}
					</Hint>
					{editable && (
						<SelectAllLabel>
							<input
								type="checkbox"
								checked={selectableRows.length > 0 && checkedCount === selectableRows.length}
								disabled={selectableRows.length === 0}
								ref={(el) => {
									if (el) el.indeterminate = checkedCount > 0 && checkedCount < selectableRows.length;
								}}
								onChange={(e) =>
									setChecked(
										selectableRows.map((mp) => mp.maalepunktId),
										e.target.checked
									)
								}
							/>
							Velg alle
						</SelectAllLabel>
					)}
					<List>
						<HeaderRow $editable={editable}>
							{editable && <div />}
							<div>Målepunkt-ID</div>
							<div>Målernummer</div>
							<div>Adresse</div>
							<RightAligned>Forventet årsforbruk</RightAligned>
							{!editable && <div>Sikringsprodukt</div>}
						</HeaderRow>
						{rows.map((mp) => (
							<Row
								key={mp.maalepunktId}
								$editable={editable}
								$selected={mp.selected}
								$blocked={mp.check === MeteringPointCheck.BlockedForSwitching}
							>
								{editable && (
									<input
										type="checkbox"
										aria-label={`Ta med målepunkt ${mp.maalepunktId}`}
										checked={isSelectable(mp) && mp.checked}
										disabled={!isSelectable(mp)}
										onChange={(e) => setChecked([mp.maalepunktId], e.target.checked)}
									/>
								)}
								<div>
									{mp.maalepunktId}
									{mp.addedManually && <ManualTag>Lagt til manuelt</ManualTag>}
								</div>
								<div>{mp.maalernummer || "–"}</div>
								<div>
									<AddressOrCheck row={mp} prisomraade={prisomraade} onRetry={() => lookups.get(mp.maalepunktId)?.refetch()} />
								</div>
								<RightAligned>
									{isDefined(mp.forventetAarsforbrukKwh) ? `${formatNumber(mp.forventetAarsforbrukKwh)} kWh` : "Ingen data"}
								</RightAligned>
								{!editable && <div>{mp.harSikringsprodukt ? "Har produktet" : <CheckError>Mangler</CheckError>}</div>}
							</Row>
						))}
					</List>
				</>
			)}

			{editable && (
				<AddSection>
					{adding ? (
						<>
							<Label htmlFor="lim-inn-maalepunkt" style={{ fontSize: "0.875rem", marginBottom: "4px" }}>
								Legg til målepunkt
							</Label>
							<Hint style={{ margin: "0 0 8px" }}>
								Lim inn målepunkt-ID-er, f.eks. en kolonne fra Excel. Målepunkt som allerede er i listen hoppes over.
							</Hint>
							<Textarea
								id="lim-inn-maalepunkt"
								rows={4}
								autoFocus
								value={pasted}
								placeholder="Lim inn en eller flere målepunkt-ID-er"
								onChange={(e) => setPasted(e.target.value)}
							/>
							<AddButtons>
								<Button type="button" label="Legg til" disabled={!pasted.trim()} onClick={addPasted} />
								<Button
									secondary
									type="button"
									label="Avbryt"
									onClick={() => {
										setAdding(false);
										setPasted("");
									}}
								/>
							</AddButtons>
						</>
					) : (
						<Button
							secondary
							type="button"
							label="Legg til målepunkt"
							onClick={() => {
								setAdding(true);
								setLastAdded(null);
							}}
						/>
					)}
					{lastAdded && <AddResult result={lastAdded} />}
				</AddSection>
			)}
		</div>
	);
}

/** Adressen, eller hvorfor målepunktet ikke kan være med. */
function AddressOrCheck({ row, prisomraade, onRetry }: Readonly<{ row: CustomerMeteringPointRow; prisomraade: string; onRetry: () => void }>) {
	switch (row.check) {
		case MeteringPointCheck.Loading:
			return <>Henter målepunktet …</>;
		case MeteringPointCheck.NotFound:
			return <CheckError>Fant ikke målepunktet.</CheckError>;
		case MeteringPointCheck.BlockedForSwitching:
			return <CheckError>Målepunktet er blokkert for bytte, og kan ikke få kontrakt.</CheckError>;
		case MeteringPointCheck.WrongPriceArea:
			return (
				<CheckError>
					Ligger i {row.prisomraade || "ukjent prisområde"}, ikke i {prisomraade}.
				</CheckError>
			);
		case MeteringPointCheck.Failed:
			return (
				<CheckError>
					Kunne ikke hente målepunktet.{" "}
					<a
						href="#"
						onClick={(e) => {
							e.preventDefault();
							onRetry();
						}}
					>
						Prøv igjen
					</a>
				</CheckError>
			);
		case MeteringPointCheck.Ok:
			return <>{formatAddress(row.adresse) || "Ukjent adresse"}</>;
	}
}

function AddResult({ result }: Readonly<{ result: ParsedMaalepunktIds }>) {
	const { newIds, duplicateCount, invalid } = result;
	return (
		<>
			<Hint>
				{newIds.length} målepunkt lagt til.
				{duplicateCount > 0 && ` ${duplicateCount} var allerede i listen og ble hoppet over.`}
			</Hint>
			{invalid.length > 0 && (
				<ErrorText style={{ marginTop: "4px" }}>
					Ikke gyldige målepunkt-ID-er (18 siffer som starter på 7070575000, med riktig kontrollsiffer), ignorert: {invalid.join(", ")}
				</ErrorText>
			)}
		</>
	);
}

const COLUMNS = "minmax(150px, 1.2fr) minmax(110px, 1fr) minmax(160px, 2fr) 130px";
// Sjekkbokskolonnen vises bare når listen kan endres. Uten kontrakt vises i stedet om målepunktet har sikringsproduktet.
const columns = (editable: boolean) => (editable ? `32px ${COLUMNS}` : `${COLUMNS} 110px`);

// Kunder kan ha mange målepunkt, så listen får fast makshøyde og egen rulling.
const List = styled.div`
	max-height: 280px;
	overflow: auto;
	border-radius: 9px;
	box-shadow: inset 0 0 0 1px ${colors.grey300};
	font-size: 0.8125rem;
`;

const HeaderRow = styled.div<{ $editable: boolean }>`
	display: grid;
	grid-template-columns: ${(props) => columns(props.$editable)};
	gap: 8px;
	padding: 8px 12px;
	/* Når listen er smalere enn kolonnene, rulles den sidelengs. Uten min-width stopper bakgrunnen ved listens kant. */
	min-width: min-content;
	position: sticky;
	top: 0;
	background: ${colors.white};
	font-weight: 700;
	font-size: 0.75rem;
	align-items: center;
	color: ${colors.grey800};
	box-shadow: inset 0 -1px 0 ${colors.grey300};
`;

const Row = styled.div<{ $editable: boolean; $selected: boolean; $blocked: boolean }>`
	display: grid;
	grid-template-columns: ${(props) => columns(props.$editable)};
	gap: 8px;
	padding: 6px 12px;
	min-width: min-content;
	background: ${(props) => (props.$blocked ? colors.coral100 : "transparent")};
	/* Toppjustert, så rader der adressen brytes over flere linjer ikke får cellene til å stå i bølger. */
	align-items: start;
	color: ${(props) => (props.$selected ? "inherit" : colors.grey800)};

	/* Uten min-width krymper ikke grid-cellene, og et langt målernummer flyter inn i adressen. */
	& > div {
		min-width: 0;
		overflow-wrap: anywhere;
	}

	&:not(:last-child) {
		box-shadow: inset 0 -1px 0 ${colors.grey200};
	}

	/* Standard disabled-sjekkboks er nesten lik en aktiv, så den tones tydelig ned. */
	& > input:disabled {
		opacity: 0.35;
		cursor: not-allowed;
	}
`;

const SelectAllLabel = styled.label`
	display: inline-flex;
	align-items: center;
	gap: 8px;
	margin: 0 0 8px 12px;
	font-size: 0.8125rem;
	cursor: pointer;
`;

const AddButtons = styled.div`
	display: flex;
	gap: 8px;
	margin-top: 8px;
`;

const RightAligned = styled.div`
	text-align: right;
`;

const MissingProductWarning = styled.div`
	margin-bottom: 12px;
`;

const ManualTag = styled.div`
	font-size: 0.75rem;
	color: ${colors.grey800};
`;

const CheckError = styled.span`
	color: ${colors.coral};
`;

const AddSection = styled.div`
	margin-top: 16px;
`;

const Textarea = styled.textarea`
	width: 100%;
	box-sizing: border-box;
	font-family: inherit;
	font-size: 0.8125rem;
	resize: vertical;
`;

const InfoText = styled.p`
	font-size: 0.8125rem;
	color: ${colors.grey900};
	margin: 0 0 8px;
`;

const ErrorText = styled.p`
	font-size: 0.8125rem;
	color: ${colors.coral};
	margin: 0;
`;
